import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const SESSION_KEY = 'sherak:promo-offer-popup:seen';

/**
 * The section IDs of every cart on the page, so an add-to-cart response carries
 * the HTML needed to refresh them.
 *
 * Kept here rather than in the theme's shared utilities so a theme update can't
 * take this section's add-to-cart with it. `free-shipping-bar.js` carries its
 * own copy for the same reason — change both together.
 *
 * @returns {string[]} The section IDs.
 */
function cartSectionIds() {
  /** @type {Set<string>} */
  const sectionIds = new Set();

  for (const cartItems of document.querySelectorAll('cart-items-component')) {
    if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
      sectionIds.add(cartItems.dataset.sectionId);
    }
  }

  return Array.from(sectionIds);
}

/**
 * Reads the cart's new item count out of a re-rendered cart section, which
 * carries it in a hidden element for exactly this purpose.
 *
 * @param {Record<string, string> | undefined} sections - The sections returned by the cart.
 * @param {string[]} sectionIds - The section IDs that were requested.
 * @returns {number | null} The new item count, or null if no section carried one.
 */
function parseCartItemCount(sections, sectionIds) {
  for (const sectionId of sectionIds) {
    const html = sections?.[sectionId];

    if (!html) continue;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const count = parsed.querySelector('[ref="cartItemCount"]')?.textContent?.trim();

    if (count) return Number.parseInt(count, 10);
  }

  return null;
}

/**
 * Turns one end of the countdown window into an absolute moment.
 *
 * Two readings of the same typed time, and the merchant chooses which:
 *
 * - `store`: resolved in Liquid against the shop's timezone, so every
 *   visitor on earth counts down to the same instant. "Ends 11:59 PM" means
 *   the moment the offer is actually switched off.
 * - `visitor`: rebuilt here from the wall clock numbers, which `new Date`
 *   reads in whatever zone the browser is in, so each visitor gets 11:59 PM
 *   by their own clock. Later for anyone west of the shop.
 *
 * Built from components rather than parsed from a string: an ISO string
 * without an offset is read as local time, but one that is date-only is read
 * as UTC, and the difference between those two is a silent day's error.
 *
 * @param {HTMLElement} bar - The countdown bar.
 * @param {'start' | 'end'} which - Which end of the window.
 * @returns {number} Milliseconds since the epoch, or 0 if unset.
 */
function resolveMoment(bar, which) {
  const epoch = Number(bar.dataset[which] || 0) * 1000;

  if (bar.dataset.timeBase !== 'visitor') return epoch;

  // Nothing to rebuild from, or no moment set at all: the store's reading
  // stands rather than the bar silently losing its deadline.
  const parts = (bar.dataset[`${which}Parts`] || '').split(',').map(Number);

  if (!epoch || parts.length !== 6 || parts.some(Number.isNaN)) return epoch;

  const [year, month, day, hours, minutes, seconds] = parts;

  return new Date(year, month - 1, day, hours, minutes, seconds).getTime();
}

/**
 * Formats a duration as DD:HH:MM:SS, dropping the days segment once it hits
 * zero so a short sale reads as HH:MM:SS.
 *
 * @param {number} ms - Milliseconds remaining.
 * @returns {string} The formatted duration.
 */
function formatRemaining(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');

  const parts = [hours, minutes, seconds].map(pad);

  if (days > 0) parts.unshift(pad(days));

  return parts.join(':');
}

/**
 * A timed promotional popup that offers one-click add to cart for a short list
 * of products. Extends the theme's DialogComponent so it inherits focus
 * trapping, scroll locking, click-outside and escape handling.
 *
 * @typedef {object} PromoOfferPopupRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class PromoOfferPopupComponent extends DialogComponent {
  /** @type {number | undefined} */
  #openTimeout;

  /** @type {number | undefined} */
  #countdownInterval;

  /** @type {boolean} */
  #adding = false;

  connectedCallback() {
    super.connectedCallback();

    // Never interrupt a merchant editing the section in the theme editor.
    if (window.Shopify?.designMode) return;

    if (this.#hasBeenSeen()) return;

    const delay = Number(this.dataset.delay ?? 10) * 1000;

    this.#openTimeout = setTimeout(() => {
      // A visitor who already opened the cart, or navigated away, is mid-task —
      // don't talk over them.
      if (document.visibilityState !== 'visible') return;
      if (document.querySelector('dialog[open]')) return;

      this.#markAsSeen();
      this.#startCountdown();
      this.showDialog();
    }, delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#openTimeout) clearTimeout(this.#openTimeout);
    if (this.#countdownInterval) clearInterval(this.#countdownInterval);
  }

  /**
   * Ticks the countdown bar. Only runs once the popup is about to open, so an
   * unseen popup costs nothing.
   */
  #startCountdown() {
    const bar = this.querySelector('[data-promo-offer-countdown]');

    if (!(bar instanceof HTMLElement)) return;

    const start = resolveMoment(bar, 'start');
    const end = resolveMoment(bar, 'end');

    if (!end) return;

    const timeElement = bar.querySelector('[data-promo-offer-countdown-time]');

    const tick = () => {
      const now = Date.now();

      // Before the window opens: stay hidden but keep checking, so the bar
      // appears on its own if the start time passes while the popup is up.
      if (start && now < start) {
        bar.hidden = true;
        return;
      }

      if (now >= end) {
        bar.hidden = true;

        if (this.#countdownInterval) {
          clearInterval(this.#countdownInterval);
          this.#countdownInterval = undefined;
        }

        return;
      }

      if (timeElement) timeElement.textContent = formatRemaining(end - now);
      bar.hidden = false;
    };

    tick();
    this.#countdownInterval = setInterval(tick, 1000);
  }

  #hasBeenSeen() {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      // Private browsing or blocked storage: fall back to showing it once per
      // page load rather than erroring out.
      return false;
    }
  }

  #markAsSeen() {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // Ignore — the popup simply isn't suppressed on the next page view.
    }
  }

  /**
   * Dismisses the popup without adding anything.
   */
  dismiss = () => {
    this.closeDialog();
  };

  /**
   * Adds the clicked product to the cart, then hands off to the cart drawer.
   *
   * @param {MouseEvent & { target: HTMLElement }} event - The click event.
   */
  addToCart = async (event) => {
    const button = event.target.closest('[data-variant-id]');

    if (!(button instanceof HTMLElement)) return;

    const variantId = button.dataset.variantId;

    if (!variantId || this.#adding) return;

    this.#adding = true;
    this.#setBusy(button, true);

    try {
      // Asking for the cart sections back gets the drawer its new markup in the
      // same round trip, and carries the cart's new item count with it.
      const sectionIds = cartSectionIds();
      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });

      // 'json' keeps the application/json Content-Type that a JSON string body
      // needs — the 'javascript' variant strips it for FormData callers.
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const result = await response.json();

      if (result.status) {
        // Shopify returns `status` only on errors (e.g. sold out).
        this.#showError(button, result.description || result.message);
        return;
      }

      // Let the rest of the theme (cart bubble, cart items, drawer) react.
      document.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'promo-offer-popup',
          // The cart icon sets its bubble to this number, so it has to be the
          // cart's new total rather than the one item just added.
          itemCount: parseCartItemCount(result.sections, sectionIds) ?? 1,
          variantId,
          sections: result.sections,
        })
      );

      await this.closeDialog();
      this.#openCartDrawer();
    } catch (error) {
      console.error('Promo offer popup: add to cart failed', error);
      this.#showError(button, this.dataset.errorText || 'Could not add to cart. Please try again.');
    } finally {
      this.#adding = false;
      this.#setBusy(button, false);
    }
  };

  /**
   * The drawer only auto-opens when the theme setting is enabled, so open it
   * explicitly. Falls back to the cart page if the drawer isn't on the page.
   */
  #openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');

    if (drawer && typeof (/** @type {any} */ (drawer).open) === 'function') {
      /** @type {any} */ (drawer).open();
      return;
    }

    window.location.href = Theme.routes.cart_url || '/cart';
  }

  /**
   * @param {HTMLElement} button - The button that was clicked.
   * @param {boolean} busy - Whether the button is mid-request.
   */
  #setBusy(button, busy) {
    button.toggleAttribute('disabled', busy);
    button.classList.toggle('promo-offer__item--loading', busy);
  }

  /**
   * @param {HTMLElement} button - The button that was clicked.
   * @param {string} message - The message to display.
   */
  #showError(button, message) {
    const errorElement = this.querySelector('[data-promo-offer-error]');

    if (errorElement) {
      errorElement.textContent = message;
      errorElement.removeAttribute('hidden');
    }

    button.classList.remove('promo-offer__item--loading');
  }
}

if (!customElements.get('promo-offer-popup-component')) {
  customElements.define('promo-offer-popup-component', PromoOfferPopupComponent);
}
